import {
  notificationKeys,
  type NotificationChannel,
  type NotificationPreferenceOverride,
  type NotificationPreferences,
  type NotificationType,
} from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { retrying } from '@/app/retrying'
import { useApi } from '@/app/api'
import { problemDetail } from '../internal/problem.ts'

export interface UseNotificationPreferencesOptions {
  readonly ledgerId: () => string | undefined
}

export interface UseNotificationPreferences {
  readonly preferences: ComputedRef<NotificationPreferences | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly isSaving: ComputedRef<boolean>
  readonly saveError: ComputedRef<Error | null>
  readonly saveErrorDetail: ComputedRef<string | undefined>
  retry(): void
  setChannelEnabled(type: NotificationType, channel: NotificationChannel, enabled: boolean): Promise<void>
  setShowAmounts(channel: NotificationChannel, showAmounts: boolean): Promise<void>
}

/** One row per legal (type, channel) pair, plus the per-channel figures switch - GET/PUT notification-preferences. */
export function useNotificationPreferences(
  options: UseNotificationPreferencesOptions,
): UseNotificationPreferences {
  const api = useApi()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: computed(() => notificationKeys.preferences(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.notifications.preferences(options.ledgerId()!, signal),
    enabled: () => options.ledgerId() !== undefined,
    retry: false,
  })

  const mutation = useMutation({
    mutationFn: (update: {
      overrides: readonly NotificationPreferenceOverride[]
      showAmounts: Record<NotificationChannel, boolean>
    }) => api.notifications.replacePreferences(options.ledgerId()!, update),
    onSuccess: (result) => {
      queryClient.setQueryData(notificationKeys.preferences(options.ledgerId()!), result)
    },
  })

  function overridesFor(
    current: NotificationPreferences,
    type: NotificationType,
    channel: NotificationChannel,
    enabled: boolean,
  ) {
    return current.preferences
      .filter(
        (preference) =>
          preference.source === 'OVERRIDE' && !(preference.type === type && preference.channel === channel),
      )
      .map((preference) => ({
        type: preference.type,
        channel: preference.channel,
        enabled: preference.enabled,
      }))
      .concat([{ type, channel, enabled }])
  }

  return {
    preferences: computed(() => query.data.value),
    isLoading: computed(() => query.isPending.value),
    error: computed(() => query.error.value),
    retrying: retrying(query),
    isSaving: computed(() => mutation.isPending.value),
    saveError: computed(() => mutation.error.value),
    saveErrorDetail: computed(() => problemDetail(mutation.error.value)),
    retry: () => {
      void query.refetch()
    },
    setChannelEnabled: (type, channel, enabled) => {
      const current = query.data.value
      if (current === undefined) return Promise.resolve()
      return mutation
        .mutateAsync({
          overrides: overridesFor(current, type, channel, enabled),
          showAmounts: { ...current.showAmounts },
        })
        .then(
          () => undefined,
          () => undefined,
        )
    },
    setShowAmounts: (channel, showAmounts) => {
      const current = query.data.value
      if (current === undefined) return Promise.resolve()
      return mutation
        .mutateAsync({
          overrides: current.preferences
            .filter((preference) => preference.source === 'OVERRIDE')
            .map((preference) => ({
              type: preference.type,
              channel: preference.channel,
              enabled: preference.enabled,
            })),
          showAmounts: { ...current.showAmounts, [channel]: showAmounts },
        })
        .then(
          () => undefined,
          () => undefined,
        )
    },
  }
}
